/**
 * Shows the selected element's name, class and property sets.
 *
 * Every value here originates in the uploaded IFC, so it is untrusted text and
 * is rendered exclusively via textContent (CODE_RULES.md §7).
 */
import { clear, el, mount } from "../components/dom";
import type { SelectedElement } from "../../viewer/selection";

export interface PropertiesPanel {
  readonly root: HTMLElement;
  show(element: SelectedElement): void;
  showEmpty(): void;
}

export function createPropertiesPanel(): PropertiesPanel {
  const root = el("section", "glass enter flex flex-col gap-2 p-3 min-h-0");
  const header = el("div", "px-1");
  mount(header, el("span", "panel-title", "Element"));

  const body = el("div", "panel-body flex flex-col gap-3");
  mount(root, header, body);

  function showEmpty(): void {
    clear(body);
    mount(
      body,
      el("p", "px-1 py-2 text-xs text-[var(--color-ink-300)]", "Click an element in the model."),
    );
  }

  showEmpty();

  return {
    root,
    showEmpty,
    show(element) {
      clear(body);

      const heading = el("div", "px-1");
      mount(
        heading,
        el("p", "text-sm font-medium leading-tight", element.name),
        el("p", "text-xs text-[var(--color-ink-300)] mt-0.5", element.category),
      );
      body.appendChild(heading);

      if (element.sets.length === 0) {
        mount(
          body,
          el("p", "px-1 text-xs text-[var(--color-ink-300)]", "No property sets on this element."),
        );
        return;
      }

      for (const set of element.sets) {
        body.appendChild(renderSet(set.name, set.rows));
      }
    },
  };
}

function renderSet(name: string, rows: readonly { name: string; value: string }[]): HTMLElement {
  const group = el("div", "flex flex-col gap-0.5");
  mount(group, el("p", "panel-title px-1 pt-1", name));

  for (const row of rows) {
    const line = el("div", "flex items-baseline gap-2 px-1 py-0.5 text-xs");
    const key = el("span", "text-[var(--color-ink-300)] shrink-0 max-w-[45%] truncate", row.name);
    key.title = row.name;
    const value = el("span", "text-right ml-auto break-words", row.value);
    value.title = row.value;
    mount(line, key, value);
    group.appendChild(line);
  }
  return group;
}
